import { useEffect, useRef, useState } from "react"
import type { Consumer, Subscription } from "@rails/actioncable"
import { getConsumer } from "@/lib/cable"
import type { StreamEvent } from "@/types/chat"

export type CableStatus = "idle" | "connecting" | "connected" | "rejected" | "disconnected"

interface UseActionCableOptions {
  chatId: number
  onEvent: (event: StreamEvent) => void
  consumer?: Consumer
  /** Optional status mirror for callers that must wait until subscribed. */
  onStatus?: (status: CableStatus) => void
}

/**
 * Subscribes to the ChatChannel for a given chat and forwards parsed JSON
 * stream events to `onEvent`. Cleans up the subscription on unmount or when
 * the chat id changes.
 *
 * Returns the live subscription status so callers (e.g. Archie) can wait for
 * `connected` before enqueueing work that broadcasts on the same stream.
 */
export function useActionCable({
  chatId,
  onEvent,
  consumer,
  onStatus,
}: UseActionCableOptions): CableStatus {
  const onEventRef = useRef(onEvent)
  onEventRef.current = onEvent
  const onStatusRef = useRef(onStatus)
  onStatusRef.current = onStatus

  const [status, setStatus] = useState<CableStatus>("idle")

  useEffect(() => {
    if (!chatId || chatId <= 0) {
      setStatus("idle")
      onStatusRef.current?.("idle")
      return
    }

    setStatus("connecting")
    onStatusRef.current?.("connecting")

    const cable = consumer ?? getConsumer()
    const subscription: Subscription = cable.subscriptions.create(
      { channel: "ChatChannel", chat_id: chatId },
      {
        connected() {
          setStatus("connected")
          onStatusRef.current?.("connected")
        },
        disconnected() {
          setStatus("disconnected")
          onStatusRef.current?.("disconnected")
        },
        rejected() {
          setStatus("rejected")
          onStatusRef.current?.("rejected")
        },
        received(data: StreamEvent) {
          onEventRef.current(data)
        },
      }
    )

    return () => {
      subscription.unsubscribe()
      setStatus("idle")
      onStatusRef.current?.("idle")
    }
  }, [chatId, consumer])

  return status
}
